import { RELS_TYPES, RelsTypes } from 'domain/hooks/api/api';
import { Layout, Page } from 'domain/models';
import { Identifiable } from 'domain/models/base.model';
import { PagedQueryParams } from 'domain/utils/query.utils';
import { AdminAlertRow } from 'pages/admin/AdminAlertsRow';
import { AlertCard } from 'pages/alerts/AlertCard';
import { DashboardCard } from 'pages/dashboard/DashboardCard';
import { ObservationCard } from 'pages/observations/ObservationCard';
import { ObservationSide } from 'pages/observations/ObservationSide';
import { UserCard } from 'pages/users/UserCard';
import { ReactNode, useEffect, useState } from 'react';
import { UseHalType } from './hal/hal.service';
import { CropCardRow } from 'pages/crops/CropCard';
import { DerivedCard } from 'pages/derived/DerivedCard';
import { PanelCard } from 'pages/panel/PanelCard';

export const useColumnHeaders = <T,>(rel: RelsTypes): (keyof T)[] => {
	switch (rel) {
		case 'adminAlerts':
			return ['sendTime', 'addressee', 'message'] as (keyof T)[];

		case 'crops':
			return [
				'name',
				'createdDate',
				'subject',
				'concept',
				'shortText',
				'comments',
			] as (keyof T)[];

		case 'derived':
			return [
				'name',
				'description',
				'startDate',
				'finishDate',
				'min',
				'max',
			] as (keyof T)[];
		default:
			return [] as (keyof T)[];
	}
};

const getComponentByRel = (rel: RelsTypes, item: any) => {
	switch (rel) {
		case 'dashboards':
			return <DashboardCard item={item} />;
		case 'alerts':
			return <AlertCard item={item} />;
		case 'derived':
			return <DerivedCard item={item} />;
		case 'stations':
			return <ObservationCard item={item} />;
		case 'crops':
			return <CropCardRow item={item} />;
		case 'users':
			return <UserCard item={item} />;
		case 'panel':
			return <PanelCard item={item} />;
		case 'adminAlerts':
			return <AdminAlertRow item={item} />;
		default:
			return <>{rel} not found</>; // FIXME: This should be another
	}
};

const getSideComponentByRel = (rel: RelsTypes): ReactNode => {
	switch (rel) {
		case 'stations':
			return <ObservationSide />;
		default:
			return null;
	}
};

const getLayoutByRel = (rel: RelsTypes): Layout => {
	switch (rel) {
		case 'stations':
			return Layout.Side;
		case 'adminAlerts':
		case 'crops':
		case 'derived':
			return Layout.Table;
		default:
			return Layout.Card;
	}
};

export const useApiRel = <T extends Identifiable>(
	rel: RelsTypes
): UseHalType<T> => {
	const service = RELS_TYPES[rel]();
	return service as UseHalType<any>;
};
export const INITIAL_PAGINATION: Page<unknown> = {
	content: [],
	number: 0,
	numberOfElements: 0,
	empty: true,
	size: 20,
	totalPages: 0,
	totalElements: 0,
};
export const useApiList = (rel: RelsTypes, filter?: PagedQueryParams) => {
	const halService = useApiRel(rel);
	const [loading, setLoading] = useState(false);
	const [error, setError] = useState(false);
	const [nodeList, setNodeList] = useState<ReactNode[]>([]);
	const [page, setPage] = useState<Page<unknown>>(INITIAL_PAGINATION);
	const [side, setSide] = useState<ReactNode>();

	useEffect(() => {
		// Set loading and nodeList as empty
		setLoading(true);
		setNodeList([]);
		halService
			.find(filter)
			.then((list) => {
				setNodeList(
					list.content.map((item) => {
						return getComponentByRel(rel, item);
					})
				);
				setSide(getSideComponentByRel(rel));
				setPage(list);
				setLoading(false);
			})
			.catch((err) => {
				setLoading(false);
				setError(true);
			});
	}, [rel, filter]);

	return {
		list: nodeList,
		side,
		cardLayout: getLayoutByRel(rel),
		page,
		loading,
		error,
	};
};
